/**
 * Utility functions for SuperSkuMiniSwatches component
 */

/**
 * Determines if the "more options" link should be displayed
 * @param {number} numberOfOptions - Total number of options available
 * @param {Array} availableSwatches - Array of available swatch objects
 * @param {number} numberOfMiniSwatchesToDisplay - Maximum number of swatches to display
 * @returns {boolean} Whether to show the more options link
 */
export const shouldShowMoreOptionsLink = (numberOfOptions, availableSwatches, numberOfMiniSwatchesToDisplay) => {
  return numberOfOptions > 1
         && (numberOfOptions >= numberOfMiniSwatchesToDisplay
         || availableSwatches?.length < numberOfOptions);
};

/**
 * Calculates the "more options" text based on available options and displayed swatches
 * @param {number} totalNumberOfOptionsAvailable - Total number of options available
 * @param {number} swatchesCount - Number of swatches currently displayed
 * @returns {string|null} The text to display or null if no text should be shown
 */
export const getMoreOptionsText = (totalNumberOfOptionsAvailable, swatchesCount) => {
  if (totalNumberOfOptionsAvailable <= 0) {
    return '+more options';
  }

  const remainingOptions = totalNumberOfOptionsAvailable - swatchesCount;
  // No remaining options to show
  if (remainingOptions <= 0) {
    return null;
  }

  // Show count of remaining options
  return swatchesCount >= 1
    ? `+${remainingOptions} more`
    : `+${remainingOptions} more options`;
};

/**
 * Generates CSS classes for the hovered swatch label
 * @param {boolean} removeStaticHeight - Whether to remove static height styles
 * @returns {string} CSS class string
 */
export const getHoveredSwatchLabelClasses = (removeStaticHeight) => {
  return [
    `${removeStaticHeight ? '' : 'sui-h-4'}`,
    'sui-pt-1 sui-font-regular sui-text-xs sui-text-subtle'
  ].join(' ');
};

/**
 * Generates CSS classes for the swatch container
 * @param {boolean} center - Whether to center the swatches
 * @param {boolean} removeStaticHeight - Whether to remove static height styles
 * @returns {string} CSS class string
 */
export const getSskuSwatchContainerClasses = (center, removeStaticHeight) => {
  return [
    'sui-inline-flex sui-flex-wrap sui-items-center sui-pt-1 sui-pb-1',
    `${center ? 'sui-justify-center' : 'sui-justify-start'}`,
    `${removeStaticHeight ? '' : 'sui-min-h-4'}`
  ].join(' ');
};

/**
 * Generates CSS classes for the options link
 * @param {boolean} removeStaticHeight - Whether to remove static height styles
 * @returns {string} CSS class string
 * @param {boolean} isMobile - Whether the current view is mobile
 */
export const getOptionsLinkClasses = (removeStaticHeight, isMobile) => {
  return [
    `${removeStaticHeight ? '' : 'sui-h-4'}`,
    `${isMobile ? 'sui-text-xs' : 'sui-text-sm'}`,
    'sui-content-center sui-font-regular sui-text-primary sui-underline'
  ].join(' ');
};

/**
 * Detects if the browser supports CSS Container Queries
 * Uses CSS.supports() for reliable feature detection
 * @returns {boolean} True if container queries are supported
 * @example
 * if (hasContainerQuerySupport()) {
 *   // Use container query classes
 * } else {
 *   // Fall back to viewport-based media queries
 * }
 */
export const hasContainerQuerySupport = () => {
  // Check if we're in a browser environment (SSR safety)
  if (typeof window === 'undefined' || typeof CSS === 'undefined') {
    return false;
  }

  try {
    // CSS.supports() - Most reliable for modern browsers
    if (CSS.supports && typeof CSS.supports === 'function') {
      // Check for the container-type property (core container query feature)
      return CSS.supports('container-type', 'inline-size');
    }
    return false;
  } catch (error) {
    // If any error occurs, assume no support
    return false;
  }
};

/**
 * Generates responsive CSS classes for swatch wrapper based on index
 * @param {number} swatchIndex - The index of the swatch (0-based, should be 0-4)
 * @param {boolean} [hasContainerSupport=false] - Whether browser supports container queries
 * @returns {string} CSS class string with responsive visibility
 * @example
 * // For modern browsers with container query support
 * getSwatchWrapperClasses(2, true) // 'sui-mr-2 sui-hidden @[202px]/ssms:sui-block'
 *
 * // Fallback for older browsers
 * getSwatchWrapperClasses(2, false) // 'sui-mr-2 sui-hidden sm:sui-block'
 */
export const getSwatchWrapperClasses = (swatchIndex, hasContainerSupport = false) => {
  // Input validation - return safe default for invalid inputs
  if (typeof swatchIndex !== 'number' || swatchIndex < 0) {
    return 'sui-mr-2 sui-block';
  }

  const defaultClasses = 'sui-mr-1 sui-pr-2px';

  if (hasContainerSupport) {
    // Modern browsers: Use container queries for precise container-based responsive behavior
    if (swatchIndex === 2) {
      return `${defaultClasses} sui-hidden @[202px]/ssms:sui-block`;
    }
    if (swatchIndex === 3) {
      return `${defaultClasses} sui-hidden @[256px]/ssms:sui-block`;
    }
    if (swatchIndex === 4) {
      return `${defaultClasses} sui-hidden @[314px]/ssms:sui-block`;
    }
    return `sui-block ${defaultClasses}`;
  }

  // Fallback: Show additional swatches based on viewport size instead of container size
  // These breakpoints approximate when containers would typically be large enough
  // ~640px viewport
  if (swatchIndex === 2) {
    return `${defaultClasses} sui-hidden sm:sui-block`;
  }
  if (swatchIndex === 3) {
    return `${defaultClasses} sui-hidden md:sui-block`;
  }
  if (swatchIndex === 4) {
    return `${defaultClasses} sui-hidden lg:sui-block`;
  }
  return `sui-block ${defaultClasses}`;
};

/**
 * Determines if swatches space should be displayed in mobile view
 * @param {Array} swatchesToDisplay - Array of swatches to display
 * @param {boolean} isMobile - Whether the current view is mobile
 * @returns {boolean} Whether to display swatches space
 */
export const shouldDisplaySwatchesSpace = (swatchesToDisplay, isMobile) => {
  const displaySwatchesSpaceInMobile = swatchesToDisplay?.length >= 2;
  return isMobile ? displaySwatchesSpaceInMobile : true;
};

/**
 * Determines if swatches should be displayed (more than 1 swatch)
 * @param {Array} swatchesToDisplay - Array of swatches to display
 * @returns {boolean} Whether to display swatches
 */
export const shouldDisplaySwatches = (swatchesToDisplay) => {
  return swatchesToDisplay?.length > 1;
};

/**
 * Gets the count of swatches to display
 * @param {boolean} displaySwatches - Whether swatches should be displayed
 * @param {Array} swatchesToDisplay - Array of swatches to display
 * @returns {number} Number of swatches to display
 */
export const getSwatchesCount = (displaySwatches, swatchesToDisplay) => {
  return displaySwatches ? swatchesToDisplay.length : 0;
};

/**
 * Slices the swatches array to the specified number to display
 * @param {Array} swatches - Array of swatches from state
 * @param {Array} swatchesData - Array of swatches from data
 * @param {number} numberOfSwatchesToDisplay - Maximum number of swatches to display
 * @returns {Array} Sliced array of swatches to display
 */
export const getSwatchesToDisplay = (swatches, swatchesData, numberOfSwatchesToDisplay) => {
  const sourceSwatches = (swatches?.length ? swatches : swatchesData) || [];
  return sourceSwatches.slice(0, numberOfSwatchesToDisplay) || [];
};
